<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BarRow from 'dashboard/components-next/analytics/BarRow.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import AnalyticsAPI from 'dashboard/api/analytics';
import AssistantSettingsAPI from 'dashboard/api/assistant/settings';

const { t } = useI18n();

// Р66: расход токенов и стоимость ИИ — ТОЛЬКО платформенный супер-админ
// (владелец). Ни администратор организации, ни её супер-админ (наш
// сотрудник-настройщик) этого не видят, и галочка на организации ничего не
// открывает: это наша экономика. Зеркалит PLATFORM_ONLY_FEATURES на бэкенде.
const currentUser = useMapGetter('getCurrentUser');
const showCosts = computed(() => currentUser.value?.type === 'SuperAdmin');

// Фильтр периода — тот же date-range picker, что и в остальных отчётах
// (Р23 п.3). Диапазон хранится как [Date, Date]; аналитике отдаём ISO.
const dateRange = ref([subDays(new Date(), 6), new Date()]);
const rangeType = ref(DATE_RANGE_TYPES.LAST_7_DAYS);

const isFetching = ref(false);
const overview = ref(null);
const aiCost = ref(null);
const billing = ref(null);
const markupInput = ref('');
const isSavingMarkup = ref(false);

// Р67: контур российский — в отчётах только рубли, долларов клиент не видит
// нигде. Пересчёт делает бэкенд (Assistant::Pricing, курс из config/llm.yml) и
// кладёт <имя>_rub рядом с <имя>_usd; здесь остаётся только форматирование.
// Крупные суммы — 2 знака, «за диалог»/себестоимость — 4 (иначе всё нули).
const RUB_FORMAT = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RUB_FORMAT_PRECISE = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const formatMoney = value => {
  if (value === null || value === undefined) return '—';
  return RUB_FORMAT.format(Number(value));
};

const formatMoneyPrecise = value => {
  if (value === null || value === undefined) return '—';
  return RUB_FORMAT_PRECISE.format(Number(value));
};

const formatPercent = value => {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(1)}%`;
};

const formatNumber = value => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString();
};

// Только ИИ-KPI: deflection, передачи оператору, стоимость ИИ,
// стоимость за диалог и CSAT (если есть ответы).
const kpiCards = computed(() => {
  if (!overview.value) return [];
  const ai = overview.value.ai || {};
  return [
    {
      key: 'deflection',
      label: t('ANALYTICS.KPI.DEFLECTION'),
      value: formatPercent(ai.deflection_pct),
    },
    {
      key: 'handoffs',
      label: t('ANALYTICS.KPI.HANDOFFS'),
      value: formatNumber(ai.handoffs),
    },
    // Р64: ответы вне рабочих часов — видимая ценность для клиента (обращения,
    // которые живой оператор пропустил бы). Если рабочие часы в каналах не
    // заданы, метрику считать не из чего — показываем прочерк с подсказкой,
    // а не ноль, чтобы не выглядело «агент ночью не работал».
    {
      key: 'after_hours',
      label: t('ANALYTICS.KPI.AFTER_HOURS'),
      value: ai.after_hours_measurable
        ? formatNumber(ai.after_hours_replies)
        : '—',
      hint: ai.after_hours_measurable
        ? t('ANALYTICS.KPI.AFTER_HOURS_HINT', {
            percent: formatPercent(ai.after_hours_pct),
          })
        : t('ANALYTICS.KPI.AFTER_HOURS_NO_SCHEDULE'),
    },
    // Р26: стоимостные KPI только если разрешено (или супер-админ).
    ...(showCosts.value
      ? [
          {
            key: 'ai_cost',
            label: t('ANALYTICS.KPI.AI_COST'),
            value: formatMoney(ai.cost_rub),
          },
          {
            key: 'cost_per_conversation',
            label: t('ANALYTICS.KPI.COST_PER_CONVERSATION'),
            value: formatMoneyPrecise(ai.cost_per_conversation_rub),
          },
        ]
      : []),
    ...(ai.csat && ai.csat.responses > 0
      ? [
          {
            key: 'csat_rating',
            label: t('ANALYTICS.KPI.CSAT_RATING'),
            value: `${Number(ai.csat.average_rating).toFixed(2)} / 5`,
          },
          {
            key: 'csat_satisfaction',
            label: t('ANALYTICS.KPI.CSAT_SATISFACTION'),
            value: formatPercent(ai.csat.satisfaction_pct),
          },
        ]
      : []),
  ];
});

// Р64: клиенту — только «Активные клиенты». Себестоимость, выставленная сумма и
// наша маржа — внутренние цифры, показываются лишь при разрешённом ai_costs
// (или супер-админу): клиент не должен видеть, сколько мы зарабатываем.
const billingCards = computed(() => {
  if (!billing.value) return [];
  const usage = billing.value.usage || {};
  return [
    {
      key: 'active_clients',
      label: t('ANALYTICS.BILLING.ACTIVE_CLIENTS'),
      value: formatNumber(billing.value.active_clients),
    },
    ...(showCosts.value
      ? [
          {
            key: 'raw_cost',
            label: t('ANALYTICS.BILLING.RAW_COST'),
            value: formatMoneyPrecise(usage.raw_cost_rub),
          },
          {
            key: 'billed_cost',
            label: t('ANALYTICS.BILLING.BILLED_COST'),
            value: formatMoneyPrecise(usage.billed_cost_rub),
          },
          {
            key: 'margin',
            label: t('ANALYTICS.BILLING.MARGIN'),
            value: formatMoneyPrecise(usage.margin_rub),
          },
        ]
      : []),
  ];
});

// Курс пересчёта приходит в сводках рядом с деньгами (rub_per_usd) — показываем
// его подписью, чтобы цифры можно было сверить с прайсами провайдеров.
const RATE_FORMAT = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 4,
});

const rubPerUsd = computed(() => {
  const rate =
    billing.value?.usage?.rub_per_usd ??
    overview.value?.ai?.rub_per_usd ??
    aiCost.value?.summary?.rub_per_usd;
  return rate ? RATE_FORMAT.format(Number(rate)) : null;
});

const clientsByMonth = computed(() => {
  const rows = billing.value?.active_clients_by_month || [];
  const max = rows.reduce((acc, r) => Math.max(acc, r.active_clients), 0);
  return rows.map(r => ({ ...r, max }));
});

// Единая таблица «по моделям»: события берём из aiCost.breakdown,
// токены/себестоимость/к оплате — из billing.by_model (fallback на aiCost).
const modelRows = computed(() => {
  const costMap = {};
  (aiCost.value?.breakdown || []).forEach(row => {
    costMap[row.model] = row;
  });
  const billingMap = {};
  (billing.value?.by_model || []).forEach(row => {
    billingMap[row.model] = row;
  });
  const models = new Set([...Object.keys(costMap), ...Object.keys(billingMap)]);
  return Array.from(models)
    .map(model => {
      const cost = costMap[model] || {};
      const bill = billingMap[model] || {};
      return {
        model,
        events_count: cost.events_count ?? 0,
        total_tokens: bill.total_tokens ?? cost.total_tokens ?? 0,
        raw_cost_rub: bill.raw_cost_rub ?? cost.cost_rub ?? 0,
        billed_cost_rub:
          bill.billed_cost_rub === undefined ? null : bill.billed_cost_rub,
      };
    })
    .sort((a, b) => (b.raw_cost_rub || 0) - (a.raw_cost_rub || 0));
});

// Пикер отдаёт Date-объекты (start=startOfDay, end=endOfDay); аналитике нужен
// ISO (see AnalyticsAPI / AssistantUsageAPI).
const dateParams = () => ({
  since: dateRange.value[0].toISOString(),
  until: dateRange.value[1].toISOString(),
});

// Реактивный диапазон для секции «Использование ИИ»: пересчитывается при
// смене периода отчёта, секция перезапрашивает данные по watch.

const fetchAnalytics = async () => {
  isFetching.value = true;
  const params = dateParams();
  try {
    // Р26/Р64: aiCost (токены по моделям) отдаёт 403 без ai_costs — дёргаем
    // только при разрешении. billing доступен всем: клиенту нужны «активные
    // клиенты», а деньги из ответа вырезает бэкенд.
    const [overviewResponse, billingResponse] = await Promise.all([
      AnalyticsAPI.overview(params),
      AnalyticsAPI.billing(params),
    ]);
    overview.value = overviewResponse.data;
    billing.value = billingResponse.data;
    markupInput.value =
      billing.value.markup_pct === null ||
      billing.value.markup_pct === undefined
        ? ''
        : String(billing.value.markup_pct);
    if (showCosts.value) {
      const costResponse = await AnalyticsAPI.aiCost(params);
      aiCost.value = costResponse.data;
    } else {
      aiCost.value = null;
    }
  } catch (error) {
    useAlert(t('ANALYTICS.API.ERROR_MESSAGE'));
  } finally {
    isFetching.value = false;
  }
};

const saveMarkup = async () => {
  isSavingMarkup.value = true;
  try {
    const value = markupInput.value === '' ? null : Number(markupInput.value);
    await AssistantSettingsAPI.update({
      config: { billing_markup_pct: value },
    });
    await fetchAnalytics();
    useAlert(t('ANALYTICS.BILLING.MARKUP_SAVED'));
  } catch (error) {
    useAlert(t('ANALYTICS.BILLING.MARKUP_ERROR'));
  } finally {
    isSavingMarkup.value = false;
  }
};

// Событие пикера: value = [startDate, endDate, rangeTypeStr].
const onDateRangeChange = value => {
  const [start, end, type] = value;
  dateRange.value = [start, end];
  rangeType.value = type || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchAnalytics();
};

// Выгрузка отчёта (Р23 п.3). Серверного экспорта у аналитики нет — собираем
// CSV на клиенте (KPI + биллинг-карточки + таблица «по моделям») и отдаём
// через тот же downloadCsvFile, что и другие отчёты.
const escapeCsv = value => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadReport = () => {
  const lines = [];
  // Р26/Р64: сами карточки уже отфильтрованы по ai_costs (клиенту из биллинга
  // достаются только «Активные клиенты»), поэтому склеиваем всегда. Таблица «по
  // моделям» ниже — отдельно под флагом.
  const cards = [...kpiCards.value, ...billingCards.value];
  cards.forEach(c => lines.push([c.label, c.value].map(escapeCsv).join(',')));
  if (showCosts.value) {
    lines.push('');
    // В CSV деньги уходят «сырыми» числами (чтобы Excel считал их числами, а не
    // текстом), поэтому валюта — в заголовке колонки, а не рядом со значением.
    lines.push(
      [
        t('ANALYTICS.AI_COST.MODEL'),
        t('ANALYTICS.AI_COST.EVENTS'),
        t('ANALYTICS.AI_COST.TOKENS'),
        t('ANALYTICS.BILLING.RAW_COST_CSV'),
        t('ANALYTICS.BILLING.BILLED_COST_CSV'),
      ]
        .map(escapeCsv)
        .join(',')
    );
    modelRows.value.forEach(r =>
      lines.push(
        [
          r.model,
          r.events_count,
          r.total_tokens,
          r.raw_cost_rub,
          r.billed_cost_rub,
        ]
          .map(escapeCsv)
          .join(',')
      )
    );
  }
  const fileName = `ai-agents-report-${format(new Date(), 'dd-MM-yyyy')}.csv`;
  downloadCsvFile(fileName, lines.join('\n'));
};

onMounted(() => fetchAnalytics());
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="px-6">
      <div class="w-full max-w-5xl mx-auto pt-6 pb-5">
        <!-- Строка 1 (эталон CSAT): заголовок слева + кнопка выгрузки справа. -->
        <div class="flex items-start justify-between w-full gap-4">
          <span
            class="text-xl font-medium text-n-slate-12 min-h-10 flex items-center"
          >
            {{ t('ANALYTICS.HEADER') }}
          </span>
          <Button
            :label="t('ANALYTICS.DOWNLOAD_REPORT')"
            icon="i-ph-download-simple"
            size="sm"
            class="flex-shrink-0"
            :disabled="isFetching || !overview"
            @click="downloadReport"
          />
        </div>
        <!-- Строка 2: фильтр по дате — под заголовком (эталон CSAT-фильтров). -->
        <div class="mt-4">
          <WootDatePicker
            v-model:date-range="dateRange"
            v-model:range-type="rangeType"
            @date-range-changed="onDateRangeChange"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl h-full mx-auto pb-4">
        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div v-else-if="overview" class="flex flex-col gap-8">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div
              v-for="card in kpiCards"
              :key="card.key"
              class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
            >
              <span class="text-xs text-n-slate-11">{{ card.label }}</span>
              <span class="text-lg font-medium text-n-slate-12">
                {{ card.value }}
              </span>
              <span v-if="card.hint" class="text-xs text-n-slate-10">
                {{ card.hint }}
              </span>
            </div>
          </div>

          <!-- Р64: блок виден и клиенту — там «Активные клиенты». Заголовок и
               управление наценкой (внутренние деньги) остаются под ai_costs. -->
          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div class="flex flex-col gap-0.5">
                <h3 class="text-base font-medium text-n-slate-12">
                  {{
                    showCosts
                      ? t('ANALYTICS.COST_AND_BILLING.TITLE')
                      : t('ANALYTICS.BILLING.CLIENTS_TITLE')
                  }}
                </h3>
                <span
                  v-if="showCosts && rubPerUsd"
                  class="text-xs text-n-slate-11"
                >
                  {{ t('ANALYTICS.BILLING.RATE_HINT', { rate: rubPerUsd }) }}
                </span>
              </div>
              <div v-if="billing && showCosts" class="flex items-end gap-2">
                <label class="flex flex-col gap-1">
                  <span class="text-xs text-n-slate-11">
                    {{ t('ANALYTICS.BILLING.MARKUP_LABEL') }}
                  </span>
                  <input
                    v-model="markupInput"
                    type="number"
                    min="0"
                    step="0.1"
                    class="w-28 px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
                    :placeholder="t('ANALYTICS.BILLING.MARKUP_PLACEHOLDER')"
                  />
                </label>
                <Button
                  variant="faded"
                  color="slate"
                  size="sm"
                  :label="t('ANALYTICS.BILLING.MARKUP_SAVE')"
                  :is-loading="isSavingMarkup"
                  :disabled="isSavingMarkup"
                  @click="saveMarkup"
                />
              </div>
            </div>

            <div v-if="billing" class="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div
                v-for="card in billingCards"
                :key="card.key"
                class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
              >
                <span class="text-xs text-n-slate-11">{{ card.label }}</span>
                <span class="text-lg font-medium text-n-slate-12">
                  {{ card.value }}
                </span>
              </div>
            </div>

            <div v-if="clientsByMonth.length" class="flex flex-col gap-3 mt-2">
              <h4 class="text-sm font-medium text-n-slate-11">
                {{ t('ANALYTICS.BILLING.CLIENTS_TREND') }}
              </h4>
              <BarRow
                v-for="row in clientsByMonth"
                :key="row.month"
                :label="row.month"
                :value="row.active_clients"
                :max="row.max"
              />
            </div>

            <!-- Р64: разбивка по МОДЕЛЯМ (какие ИИ используем и сколько стоят)
                 закрыта галочкой ai_costs — клиенту эти данные не показываем. -->
            <div v-if="showCosts" class="flex flex-col gap-3 mt-2">
              <h4 class="text-sm font-medium text-n-slate-11">
                {{ t('ANALYTICS.AI_COST.BY_MODEL') }}
              </h4>
              <div v-if="modelRows.length" class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="border-b border-n-weak text-n-slate-11">
                      <th class="px-3 py-2 font-medium text-start">
                        {{ t('ANALYTICS.AI_COST.MODEL') }}
                      </th>
                      <th class="px-3 py-2 font-medium text-end">
                        {{ t('ANALYTICS.AI_COST.EVENTS') }}
                      </th>
                      <th class="px-3 py-2 font-medium text-end">
                        {{ t('ANALYTICS.AI_COST.TOKENS') }}
                      </th>
                      <th class="px-3 py-2 font-medium text-end">
                        {{ t('ANALYTICS.BILLING.RAW_COST') }}
                      </th>
                      <th class="px-3 py-2 font-medium text-end">
                        {{ t('ANALYTICS.BILLING.BILLED_COST') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in modelRows"
                      :key="row.model"
                      class="border-b border-n-weak last:border-b-0"
                    >
                      <td class="px-3 py-2 text-n-slate-12">{{ row.model }}</td>
                      <td class="px-3 py-2 text-end text-n-slate-11">
                        {{ formatNumber(row.events_count) }}
                      </td>
                      <td class="px-3 py-2 text-end text-n-slate-11">
                        {{ formatNumber(row.total_tokens) }}
                      </td>
                      <td class="px-3 py-2 text-end text-n-slate-11">
                        {{ formatMoneyPrecise(row.raw_cost_rub) }}
                      </td>
                      <td class="px-3 py-2 text-end text-n-slate-12">
                        {{ formatMoneyPrecise(row.billed_cost_rub) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="text-sm text-n-slate-11">
                {{ t('ANALYTICS.EMPTY') }}
              </p>
            </div>
          </div>

          <!-- Р64: разбивка по токенам/моделям/стоимости УБРАНА из Отчётов —
               она живёт в разделе «ИИ → Использование» и закрыта галочкой
               ai_costs. Клиент в Отчётах видит только результат работы агента,
               а не наши расходы и названия моделей. -->
        </div>
      </div>
    </main>
  </section>
</template>
